// Using a local type alias to avoid cross-package enum import issues
type NetworkingType = "PUBLIC" | "PRIVATE" | "BY_REQUEST";

type Capabilities = {
  canConnectWith?: string[];
  canChatWith?: string[];
  canMatchmakeWith?: string[];
  canAccessFloorPlan?: boolean;
  canAccessBookmarks?: boolean;
  canAccessAgenda?: boolean;
  privacySettings?: NetworkingType | null;
  canAccessAnalytics?: boolean;
  canAccessNotifications?: boolean;
  canAccessTeam?: boolean;
  canAccessProducts?: boolean;
  canAccessDocuments?: boolean;
  canAccessVideos?: boolean;
  canAccessCalendar?: boolean;
  canAccessLeads?: boolean;
  canAccessBoothAdmin?: boolean;
  exhibitorPermissions?: {
    adminSettings?: boolean;
    companySettings?: boolean;
    eventDayBadge?: boolean;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

// Narrowed string literal unions to enable IntelliSense suggestions
export type AccessFeature =
  | "floorPlan"
  | "bookmarks"
  | "agenda"
  | "networkingPrivacy"
  | "personalDetails"
  | "myBadge"
  | "myAgenda"
  | "myMeetings"
  | "myConnections"
  | "analytics"
  | "notifications"
  | "team"
  | "products"
  | "documents"
  | "videos"
  | "calendar"
  | "leads"
  | "boothAdmin"
  | "settings"
  // Nested exhibitor-specific permissions (dot-paths)
  | "exhibitorPermissions.adminSettings"
  | "exhibitorPermissions.companySettings"
  | "exhibitorPermissions.eventDayBadge";
export type InteractionAction =
  | "Connect"
  | "Chat"
  | "Matchmake"
  | "BookMeeting"
  | "NetworkingPrivacy";

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function createPermissionChecker(capabilities: Capabilities | undefined) {
  const caps = capabilities ?? {};

  const getBoolean = (key: string) => Boolean((caps as any)[key]);
  const getByPath = (obj: unknown, path: string) => {
    if (!path.includes(".")) return (obj as any)[path];
    return path.split(".").reduce<any>((acc, part) => {
      if (acc && typeof acc === "object") {
        return (acc as any)[part];
      }
      return undefined;
    }, obj as any);
  };
  const getList = (key: string) => {
    const v = (caps as any)[key];
    return Array.isArray(v) ? (v as string[]) : [];
  };

  return {
    // Boolean features: 'floorPlan' | 'bookmarks' | 'agenda'
    canAccess: (feature: AccessFeature) => {
      // Support nested dot-paths like 'exhibitorPermissions.companySettings'
      if (feature.includes(".")) {
        const value = getByPath(caps, feature);
        return Boolean(value);
      }
      return getBoolean(`canAccess${capitalize(feature)}`);
    },

    // Relationship features: 'Connect' | 'Chat' | 'Matchmake'
    canInteractWith: (action: InteractionAction, targetId: string) => {
      const key = `can${capitalize(action)}With`;
      return getList(key).includes(targetId);
    },
    getPrivacySettings: (): NetworkingType | null => {
      return caps.privacySettings ?? null;
    },

    // Optional: flatten to a set of permission strings if you like string-based checks
    toSet: () => {
      const set = new Set<string>();
      if (getBoolean("canAccessFloorPlan")) set.add("access:floorPlan");
      if (getBoolean("canAccessBookmarks")) set.add("access:bookmarks");
      if (getBoolean("canAccessAgenda")) set.add("access:agenda");
      if (getBoolean("canAccessPersonalDetails"))
        set.add("access:personalDetails");
      if (getBoolean("canAccessMyBadge")) set.add("access:myBadge");
      if (getBoolean("canAccessMyAgenda")) set.add("access:myAgenda");
      if (getBoolean("canAccessMyMeetings")) set.add("access:myMeetings");
      if (getBoolean("canAccessMyConnections")) set.add("access:myConnections");
      if (getBoolean("canAccessAnalytics")) set.add("access:analytics");
      if (getBoolean("canAccessNotifications")) set.add("access:notifications");
      if (getBoolean("canAccessTeam")) set.add("access:team");
      if (getBoolean("canAccessProducts")) set.add("access:products");
      if (getBoolean("canAccessDocuments")) set.add("access:documents");
      if (getBoolean("canAccessVideos")) set.add("access:videos");
      if (getBoolean("canAccessCalendar")) set.add("access:calendar");
      if (getBoolean("canAccessLeads")) set.add("access:leads");
      const exhibitorPerms = (caps as any).exhibitorPermissions;
      if (exhibitorPerms && typeof exhibitorPerms === "object") {
        for (const [k, v] of Object.entries(
          exhibitorPerms as Record<string, unknown>
        )) {
          if (typeof v === "boolean" && v) {
            set.add(`access:exhibitorPermissions.${k}`);
          }
        }
      }
      for (const id of getList("canConnectWith")) set.add(`connect:${id}`);
      for (const id of getList("canChatWith")) set.add(`chat:${id}`);
      for (const id of getList("canMatchmakeWith")) set.add(`matchmake:${id}`);
      for (const id of getList("canBookMeetingWith"))
        set.add(`bookmeeting:${id}`);
      return set;
    },
  };
}
